/**
 * Theme Store
 * Zustand store for light/dark mode with persistence
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ThemeMode } from '@shared/types/preferences';
import { settingsService } from '../services/settings.service';

/** Default mode */
const DEFAULT_MODE: ThemeMode = 'light';

/**
 * Hardcoded scheme sent to server for backward compatibility.
 * The frontend no longer exposes a scheme picker, but the server
 * API still expects a `theme` string field.
 */
const SERVER_SCHEME = 'success-green' as const;

interface ThemeState {
  mode: ThemeMode;
  isLoading: boolean;
  hasHydrated: boolean;

  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;

  syncWithServer: () => Promise<void>;
  saveToServer: (mode?: ThemeMode) => Promise<void>;

  setHasHydrated: (hasHydrated: boolean) => void;
}

/**
 * Apply theme mode to document
 */
function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;
  if (mode === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  root.setAttribute('data-mode', mode);
  root.setAttribute('data-theme', mode);
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: DEFAULT_MODE,
      isLoading: false,
      hasHydrated: false,

      /**
       * Set theme mode (light or dark)
       */
      setMode: (mode: ThemeMode) => {
        applyTheme(mode);
        set({ mode });
        get().saveToServer(mode).catch(console.error);
      },

      /**
       * Toggle between light and dark mode
       */
      toggleMode: () => {
        const { mode } = get();
        const newMode: ThemeMode = mode === 'light' ? 'dark' : 'light';
        get().setMode(newMode);
      },

      /**
       * Sync theme from server (for authenticated users)
       */
      syncWithServer: async () => {
        try {
          set({ isLoading: true });
          const preferences = await settingsService.getPreferences();
          const mode = preferences.mode;
          applyTheme(mode);
          set({ mode });
        } catch (error) {
          console.error('Failed to sync theme from server:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      /**
       * Save theme to server (for authenticated users)
       */
      saveToServer: async (mode?: ThemeMode) => {
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) return;

        try {
          const currentMode = mode ?? get().mode;
          await settingsService.updatePreferences({
            theme: SERVER_SCHEME,
            mode: currentMode,
          });
        } catch (error) {
          console.debug('Failed to save theme to server:', error);
        }
      },

      /**
       * Set hydration status
       */
      setHasHydrated: (hasHydrated: boolean) => {
        set({ hasHydrated });
      },
    }),
    {
      name: 'theme-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          applyTheme(state.mode);
          state.setHasHydrated(true);
        }
      },
    }
  )
);
